import axios from 'axios';
import { LEADS_API_BASE_URL } from '../config/api';

const API_BASE_URL = process.env.REACT_APP_API_URL || LEADS_API_BASE_URL;

class ChatService {
  constructor() {
    this.listeners = [];
    this.socket = null;
    this.isConnected = false;
  }

  // Initialize chat service
  initialize(userId, userType) {
    this.userId = userId;
    this.userType = userType;
    this.listeners = [];
    this.startPolling();
    this.setupStorageListener();
  }

  // Start polling for new messages
  startPolling() {
    // Disabled for demo to avoid API errors
    console.log('Message polling disabled for demo');
    return;
    
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
    }
    
    this.pollingInterval = setInterval(() => {
      this.checkForNewMessages();
    }, 3000); // Check every 3 seconds
  }

  // Stop polling
  stopPolling() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  }

  // Check for new messages
  async checkForNewMessages() {
    try {
      const response = await axios.get(`${API_BASE_URL}/chat/messages/${this.userId}/new`);
      if (response.data.success && response.data.messages.length > 0) {
        this.notifyListeners('newMessages', response.data.messages);
      }
    } catch (error) {
      console.error('Error checking for new messages:', error);
    }
  }

  // Get chat conversations
  async getConversations() {
    // Skip backend API calls for demo - use fallback directly
    console.log('Using fallback conversations for demo mode');
    
    // Fallback to stored + mock conversations
    const storedConversations = this.getStoredConversations();
    const mockConversations = this.getMockConversations();
    
    // Combine and remove duplicates
    const allConversations = [...mockConversations, ...storedConversations];
    const uniqueConversations = allConversations.filter((conv, index, self) => 
      index === self.findIndex(c => c._id === conv._id)
    );
    
    return {
      success: true,
      conversations: uniqueConversations
    };
  }

  // Get messages for a specific conversation
  async getMessages(conversationId, page = 1, limit = 50) {
    // Skip backend API calls for demo - use fallback directly
    console.log('Using fallback messages for demo mode');
    
    // Fallback to stored messages + mock messages
    const storedMessages = this.getStoredMessages(conversationId);
    const mockMessages = this.getMockMessages(conversationId);
    
    // Combine and sort by timestamp
    const allMessages = [...mockMessages, ...storedMessages]
      .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    
    return {
      success: true,
      messages: allMessages
    };
  }

  // Send a message
  async sendMessage(conversationId, content, messageType = 'text') {
    // Skip backend API calls for demo - use fallback directly
    console.log('Using fallback message sending for demo mode');
    
    try {
      
      // Use localStorage for demo
      const currentUser = JSON.parse(localStorage.getItem('currentUser'));
      const mockMessage = {
        _id: Date.now().toString(),
        conversationId,
        content,
        messageType,
        senderId: this.userId,
        senderType: this.userType,
        senderName: currentUser?.name || currentUser?.teamMemberName || 'You',
        timestamp: new Date().toISOString(),
        status: 'sent'
      };
      
      // Store message in localStorage
      this.storeMessage(mockMessage);
      
      this.notifyListeners('messageSent', mockMessage);
      return {
        success: true,
        message: mockMessage
      };
    } catch (error) {
      console.error('Error in demo message sending:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Create new conversation
  async createConversation(participantId, participantType, title) {
    try {
      // For demo purposes, create a mock conversation since we don't have proper user management
      const mockConversation = {
        _id: Date.now().toString(),
        title,
        participants: [
          { userId: this.userId, userType: this.userType, name: 'You' },
          { userId: participantId, userType: participantType, name: title.replace('Chat with ', '') }
        ],
        lastMessage: null,
        unreadCount: 0,
        createdAt: new Date().toISOString()
      };
      
      return {
        success: true,
        conversation: mockConversation
      };
      
      /* Backend conversation creation - disabled for now
      const conversationData = {
        participants: [
          { userId: this.userId, userType: this.userType },
          { userId: participantId, userType: participantType }
        ],
        title,
        createdBy: this.userId
      };

      const response = await axios.post(`${API_BASE_URL}/chat/conversations`, conversationData);
      
      if (response.data.success) {
        return {
          success: true,
          conversation: response.data.conversation
        };
      }
      
      return {
        success: false,
        error: response.data.message
      };
      */
    } catch (error) {
      console.error('Error creating conversation:', error);
      
      // Mock conversation creation to avoid errors
      const mockConversation = {
        _id: Date.now().toString(),
        title,
        participants: [
          { userId: this.userId, userType: this.userType, name: 'You' },
          { userId: participantId, userType: participantType, name: 'User' }
        ],
        lastMessage: null,
        unreadCount: 0,
        createdAt: new Date().toISOString()
      };
      
      // Store conversation for cross-dashboard sync
      this.storeConversation(mockConversation);
      
      return {
        success: true,
        conversation: mockConversation
      };
    }
  }

  // Mark messages as read
  async markAsRead(conversationId) {
    try {
      // Mock success for demo
      this.notifyListeners('messagesRead', { conversationId });
      return { success: true };
    } catch (error) {
      console.error('Error marking messages as read:', error);
      return { success: false, error: error.message };
    }
  }

  // Get online users
  async getOnlineUsers() {
    // Skip backend API calls for demo - use fallback directly
    console.log('Using fallback online users for demo mode');
    return {
      success: true,
      users: this.getMockOnlineUsers()
    };
  }

  // Add event listener
  addListener(callback) {
    this.listeners.push(callback);
  }

  // Remove event listener
  removeListener(callback) {
    this.listeners = this.listeners.filter(listener => listener !== callback);
  }

  // Notify all listeners
  notifyListeners(event, data) {
    this.listeners.forEach(callback => {
      try {
        callback(event, data);
      } catch (error) {
        console.error('Error in chat listener:', error);
      }
    });
  }

  // Mock data for development
  getMockConversations() {
    const currentUser = JSON.parse(localStorage.getItem('currentUser'));
    
    if (!currentUser) {
      return [];
    }
    
    const isAdmin = currentUser?.role === 'admin' || currentUser?.userType === 'admin';
    
    if (isAdmin) {
      return [
        {
          _id: 'conv1',
          title: 'Chat with Shubham',
          participants: [
            { userId: currentUser.id, userType: 'admin', name: currentUser.name },
            { userId: 'emp1', userType: 'employee', name: 'Shubham' }
          ],
          lastMessage: {
            content: 'I need help with lead assignment',
            timestamp: new Date(Date.now() - 30000).toISOString(),
            senderId: 'emp1'
          },
          unreadCount: 2,
          createdAt: new Date(Date.now() - 86400000).toISOString()
        },
        {
          _id: 'conv2',
          title: 'Chat with Sangram',
          participants: [
            { userId: currentUser.id, userType: 'admin', name: currentUser.name },
            { userId: 'emp2', userType: 'employee', name: 'Sangram' }
          ],
          lastMessage: {
            content: 'Thanks for the update!',
            timestamp: new Date(Date.now() - 3600000).toISOString(),
            senderId: currentUser.id
          },
          unreadCount: 0,
          createdAt: new Date(Date.now() - 172800000).toISOString()
        }
      ];
    } else {
      return [
        {
          _id: 'conv1',
          title: 'Chat with Admin',
          participants: [
            { userId: currentUser.id, userType: 'employee', name: currentUser.name },
            { userId: 'admin1', userType: 'admin', name: 'Admin' }
          ],
          lastMessage: {
            content: 'Sure, I will check that for you',
            timestamp: new Date(Date.now() - 30000).toISOString(),
            senderId: 'admin1'
          },
          unreadCount: 1,
          createdAt: new Date(Date.now() - 86400000).toISOString()
        }
      ];
    }
  }

  getMockMessages(conversationId) {
    const currentUser = JSON.parse(localStorage.getItem('currentUser'));
    
    if (!currentUser) {
      return [];
    }
    
    return [
      {
        _id: 'msg1',
        conversationId,
        content: 'Hi! I need some help with lead management.',
        messageType: 'text',
        senderId: 'emp1',
        senderType: 'employee',
        senderName: 'Shubham',
        timestamp: new Date(Date.now() - 3600000).toISOString(),
        status: 'read'
      },
      {
        _id: 'msg2',
        conversationId,
        content: 'Sure! What specific help do you need?',
        messageType: 'text',
        senderId: currentUser.id,
        senderType: currentUser.role === 'admin' ? 'admin' : 'employee',
        senderName: currentUser.name,
        timestamp: new Date(Date.now() - 3500000).toISOString(),
        status: 'read'
      },
      {
        _id: 'msg3',
        conversationId,
        content: 'I need help with lead assignment process.',
        messageType: 'text',
        senderId: 'emp1',
        senderType: 'employee',
        senderName: 'Shubham',
        timestamp: new Date(Date.now() - 30000).toISOString(),
        status: 'delivered'
      }
    ];
  }

  getMockOnlineUsers() {
    const currentUser = JSON.parse(localStorage.getItem('currentUser'));
    
    if (!currentUser) {
      return [];
    }
    
    const isAdmin = currentUser?.role === 'admin' || currentUser?.userType === 'admin';
    
    if (isAdmin) {
      return [
        { userId: 'emp1', name: 'Shubham', userType: 'employee', status: 'online', lastSeen: new Date().toISOString() },
        { userId: 'emp2', name: 'Sangram', userType: 'employee', status: 'away', lastSeen: new Date(Date.now() - 300000).toISOString() },
        { userId: 'emp3', name: 'Tushar', userType: 'employee', status: 'offline', lastSeen: new Date(Date.now() - 3600000).toISOString() }
      ];
    } else {
      return [
        { userId: 'admin1', name: 'Admin', userType: 'admin', status: 'online', lastSeen: new Date().toISOString() }
      ];
    }
  }

  // Store message in localStorage for cross-dashboard sync
  storeMessage(message) {
    try {
      const key = `chat_messages_${message.conversationId}`;
      const existingMessages = JSON.parse(localStorage.getItem(key) || '[]');
      
      // Storing message in localStorage
      
      // Check if message already exists
      const messageExists = existingMessages.some(msg => msg._id === message._id);
      if (!messageExists) {
        existingMessages.push(message);
        localStorage.setItem(key, JSON.stringify(existingMessages));
        // Message stored successfully
      }
    } catch (error) {
      console.error('Error storing message:', error);
    }
  }

  // Get stored messages for a conversation
  getStoredMessages(conversationId) {
    try {
      const key = `chat_messages_${conversationId}`;
      return JSON.parse(localStorage.getItem(key) || '[]');
    } catch (error) {
      console.error('Error getting stored messages:', error);
      return [];
    }
  }

  // Store conversation in localStorage
  storeConversation(conversation) {
    try {
      const key = 'chat_conversations';
      const existingConversations = JSON.parse(localStorage.getItem(key) || '[]');
      
      // Check if conversation already exists
      const conversationExists = existingConversations.some(conv => conv._id === conversation._id);
      if (!conversationExists) {
        existingConversations.push(conversation);
        localStorage.setItem(key, JSON.stringify(existingConversations));
      }
    } catch (error) {
      console.error('Error storing conversation:', error);
    }
  }

  // Get stored conversations
  getStoredConversations() {
    try {
      const key = 'chat_conversations';
      return JSON.parse(localStorage.getItem(key) || '[]');
    } catch (error) {
      console.error('Error getting stored conversations:', error);
      return [];
    }
  }

  // Setup storage listener for cross-tab sync
  setupStorageListener() {
    window.addEventListener('storage', (e) => {
      if (e.key && e.key.startsWith('chat_messages_')) {
        // New message added, notify listeners
        this.notifyListeners('messageReceived', {
          conversationId: e.key.replace('chat_messages_', ''),
          newValue: e.newValue
        });
      } else if (e.key === 'chat_conversations') {
        // New conversation added, notify listeners
        this.notifyListeners('conversationAdded', {
          newValue: e.newValue
        });
      }
    });
  }

  // Clear stored messages (for testing)
  clearStoredMessages(conversationId = null) {
    try {
      if (conversationId) {
        localStorage.removeItem(`chat_messages_${conversationId}`);
      } else {
        // Clear all chat messages
        Object.keys(localStorage).forEach(key => {
          if (key.startsWith('chat_messages_')) {
            localStorage.removeItem(key);
          }
        });
      }
    } catch (error) {
      console.error('Error clearing stored messages:', error);
    }
  }

  // Cleanup
  cleanup() {
    this.stopPolling();
    this.listeners = [];
  }
}

export default new ChatService();
